import React, { useState, useEffect, useContext, useRef } from 'react';
import { Switch, Route, Redirect } from 'react-router-dom';
import './../../taskpane/taskpane.css';
import Settings from '../pages/settings/Settings';
import Welcome from '../pages/home/Welcome';
import NotFound from './NotFound';
import DecidingLoader from '../pages/loader/DecidingLoader';
import { ActionContext } from '../context/ActionContext';
import { CmicContext } from '../context/CmicContext';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { baseFontFamilyStyle } from '../shared/styles';
import { DefaultButton } from '@fluentui/react/lib/Button';
import { checkNoneInlineAttachmentCount, isBGClrDark } from '../utility/UtilityFunctions';
import { COLORS } from '../utility/tasks/colors';
import { ACTION_TYPE, CMIC_TREE_NODE_TYPES, DIALOG_CONTENT_TEXT, INIT_TOAST_CONFIG } from '../shared/Constants';
import Popup from './Auth/Popup';
import Officepopup from './Auth/Officepopup';
import { MessageBarType } from '@fluentui/react';
import { isInternetAvailable } from '../shared/UtilityFunctions';

const App = props => {
	const {
		setFieldsToCreateNewPMItems,
		autoFillMailToggle,
		setAutoFillMailToggle,
		setItemFromEmail,
		attachmentData,
		actionTypeData,
	} = useContext(ActionContext);

	const { currentOfficeTheme } = props;
	const [mailChangeEventRegFlag, setMailChangeEventRegFlag] = useState(false);
	const [open, setOpen] = useState(false);
	const [dialogContentText, setDialogContentText] = useState('');
	const mailData = useRef([]);
	const actionType = useRef({});

	const { setShowToast } = useContext(CmicContext);

	const theme = useTheme();
	const fullScreenDialog = useMediaQuery(theme.breakpoints.down('xs'));

	// Keep a stable ref to the latest autoFill value so the handler doesn't need to be
	// re-registered every time the toggle changes. Also keep a stable handler ref so
	// add/remove operations use the same function reference.
	const autoFillRef = useRef(autoFillMailToggle);
	useEffect(() => {
		autoFillRef.current = autoFillMailToggle;
	}, [autoFillMailToggle]);

	// Helper to process attachments consistently across initial load and item changes
	const processAttachments = (attachments) => {
		const list = attachments || [];
		mailData.current = list;
		const noInlineAttachments = list.filter(a => !a.isInline).length === 0;

		if (
			actionType.current?.type === ACTION_TYPE.NEW &&
			(autoFillRef.current || noInlineAttachments)
		) {
			handleClickOpenDialog();
		} else {
			handleCloseChangeMailFlagOff();
		}
	};

	// Helper to fetch attachments with retry/backoff. Returns an array.
	const fetchAttachmentsForItem = async (item, maxRetries = 3) => {
		let attempt = 0;
		let lastErr = null;
		while (attempt < maxRetries) {
			try {
				if (!item) return [];
				if (typeof item.getAttachmentsAsync === 'function') {
					const res = await new Promise((resolve) => item.getAttachmentsAsync(resolve));
					if (res && res.status === Office.AsyncResultStatus.Succeeded) {
						return res.value || [];
					}
					lastErr = new Error('getAttachmentsAsync failed');
				} else if (Array.isArray(item.attachments)) {
					return item.attachments;
				} else {
					lastErr = new Error('No attachments API or property');
				}
			} catch (e) {
				lastErr = e;
			}
			// backoff before retrying
			await new Promise(r => setTimeout(r, 250 * Math.pow(2, attempt)));
			attempt += 1;
		}
		if (item && Array.isArray(item.attachments)) return item.attachments;
		console.warn('Failed to fetch attachments after retries', lastErr);
		return [];
	};

	const itemChangedHandlerRef = useRef(null);

	// Create a single stable handler that reads the latest refs. This avoids
	// adding/removing handlers repeatedly and ensures removeHandlerAsync uses
	// the same function reference.
	if (!itemChangedHandlerRef.current) {
		itemChangedHandlerRef.current = async function (eventArgs) {
			try {
				if (!isInternetAvailable(setShowToast)) return;

				// small delay to allow the mailbox context to settle after item change
				await new Promise(r => setTimeout(r, 250));

				const currentItem = Office.context.mailbox.item;
				if (!currentItem) {
					console.error('No email item selected');
					return;
				}

				// Fetch attachments using the shared helper (moved to component scope).
				const attachmentsList = await fetchAttachmentsForItem(currentItem, 3);
				processAttachments(attachmentsList);
			} catch (error) {
				console.error('Error in mail change callback:', error);
				setShowToast({
					isVisible: true,
					msg: 'Error processing email selection',
					type: MessageBarType.error,
				});
			}
		};
	}

	// Register the stable handler once when Office is ready. Use the same function
	// reference for add/remove so re-registration/removal is deterministic.
	useEffect(() => {
		Office.onReady(() => {
			// try a best-effort deregistration of any previous handlers, then add ours
			try {
				if (Office?.context?.mailbox) {
					// remove all existing handlers for the event first
					Office.context.mailbox.removeHandlerAsync(Office.EventType.ItemChanged, () => {
						// after clearing, add the stable handler
						Office.context.mailbox.addHandlerAsync(
							Office.EventType.ItemChanged,
							itemChangedHandlerRef.current,
							fnMailItemChangeRegistrationEvnt,
						);

						// Immediately try to populate attachments for the currently selected item.
						// This addresses the case where the first selected email (on app open)
						// didn't have attachments populated yet.
						try {
							const current = Office?.context?.mailbox?.item;
							if (current) {
								fetchAttachmentsForItem(current, 3).then(processAttachments).catch(e => {
									console.warn('Initial attachment fetch failed', e);
								});
							}
						} catch (e) {
							console.warn('Error during initial attachment fetch', e);
						}
					});
				}
			} catch (err) {
				// fallback: directly add handler if removal failed
				try {
					Office.context.mailbox.addHandlerAsync(
						Office.EventType.ItemChanged,
						itemChangedHandlerRef.current,
						fnMailItemChangeRegistrationEvnt,
					);

					// Also try initial fetch in the fallback path
					try {
						const current = Office?.context?.mailbox?.item;
						if (current) {
							fetchAttachmentsForItem(current, 3).then(processAttachments).catch(e => {
								console.warn('Initial attachment fetch failed', e);
							});
						}
					} catch (e) {
						console.warn('Error during initial attachment fetch', e);
					}
				} catch (e) {
					console.error('Failed to register item changed handler', e);
				}
			}
		});

		return () => {
			if (Office?.context?.mailbox && itemChangedHandlerRef.current) {
				Office.context.mailbox.removeHandlerAsync(
					Office.EventType.ItemChanged,
					() => {
						/* cleaned up */
					}
				);
			}
		};
	}, []);

	// No need to re-register handler when autoFill changes; autoFillRef keeps it up-to-date.

	const handleClickOpenDialog = () => {
		setOpen(true);
	};

	const handleCloseChangeMailFlagOff = () => {
		const item = Office?.context?.mailbox?.item;
		if (item) {
			const attachments = Array.isArray(mailData.current)
				? mailData.current.map(data => ({ ...data, originalName: data.name }))
				: (Array.isArray(item.attachments)
					? item.attachments.map(data => ({ ...data, originalName: data.name }))
					: []);

			const obj = {
				messageId: item.itemId,
				attachments,
				name: item.subject,
				from: item.from?.displayName,
				dateTimeCreated: item.dateTimeCreated,
			};

			setItemFromEmail(JSON.parse(JSON.stringify(obj)));
			setOpen(false);
		}
	};

	const handleClose = () => {
		const item = Office?.context?.mailbox?.item;
		Office?.context?.mailbox?.item?.body.getAsync(Office.CoercionType.Text, function (asyncResult) {
			if (asyncResult.status !== Office.AsyncResultStatus.Succeeded) {
				console.log('result error mail Change');
				// TODO: Handle error
			} else {
				/// Logic to refresh/override the contents(Subject & Body) of the latest selected email into RFI/Issue/Comm's Subject and Question.
				setAutoFillMailToggle(state => {
					if (state || checkNoneInlineAttachmentCount(mailData.current)) {
						const subject = (item?.subject || '').substring(0, 200);
						setFieldsToCreateNewPMItems(prev => {
							return {
								...prev,
								HTMLQuestion: asyncResult.value,
								subject,
								HTMLDescription: asyncResult.value,
								title: subject,
							};
						});
					}
					return state;
				});
			}
		});

		const attachments = Array.isArray(mailData.current)
			? mailData.current.map(data => ({ ...data, originalName: data.name }))
			: (Array.isArray(item?.attachments) ? item.attachments.map(data => ({ ...data, originalName: data.name })) : []);

		const obj = {
			messageId: item?.itemId,
			attachments,
			name: item?.subject,
			from: item?.from?.displayName,
			dateTimeCreated: item?.dateTimeCreated,
		};

		setItemFromEmail(JSON.parse(JSON.stringify(obj)));
		setOpen(false);
	};

	const mailEventDeregistrationOnRefresh = () => {
		try {
			Office?.context?.mailbox?.removeHandlerAsync(Office.EventType.ItemChanged, () => {
				// after removal, add our stable handler
				if (!mailChangeEventRegFlag && itemChangedHandlerRef.current) {
					Office?.context?.mailbox?.addHandlerAsync(
						Office.EventType.ItemChanged,
						itemChangedHandlerRef.current,
						fnMailItemChangeRegistrationEvnt,
					);
				}
			});
		} catch (err) {
			console.log('Error deregistering mail change event', err);
			setTimeout(() => {
				mailEventDeregistrationOnRefresh();
			}, 1000);
		}
	};

	useEffect(() => {
		mailData.current = [...attachmentData];
		actionType.current = actionTypeData;
		if (actionTypeData?.nodeType === CMIC_TREE_NODE_TYPES.PROJECT_FOLDER_RFI) {
			setDialogContentText(DIALOG_CONTENT_TEXT.RFI);
		}
		if (actionTypeData?.nodeType === CMIC_TREE_NODE_TYPES.PROJECT_FOLDER_ISSUE) {
			setDialogContentText(DIALOG_CONTENT_TEXT.ISSUE);
		}
	}, [attachmentData, actionTypeData]);

	useEffect(() => {
		const handleOnline = () => {
			setShowToast(INIT_TOAST_CONFIG);
		};
		const handleOffline = () => {
			setShowToast({
				isVisible: true,
				msg: 'No internet',
				type: MessageBarType.error,
			});
		};
		window.addEventListener('online', handleOnline);
		window.addEventListener('offline', handleOffline);
		return () => {
			window.removeEventListener('online', handleOnline);
			window.removeEventListener('offline', handleOffline);
		};
	}, []);

	const fnMailItemChangeRegistrationEvnt = asyncResult => {
		setMailChangeEventRegFlag(true);
	};

	return (
		<div style={{ height: '100vh', width: '100wh', backgroundColor: currentOfficeTheme.bodyBackgroundColor }}>
			<React.Fragment>
				<Dialog
					open={open}
					aria-labelledby="alert-dialog-title"
					aria-describedby="alert-dialog-description"
					fullScreen={fullScreenDialog}
				>
					<DialogTitle
						id="alert-dialog-title"
						style={{
							backgroundColor: currentOfficeTheme.bodyBackgroundColor,
							color: currentOfficeTheme.bodyForegroundColor,
							fontFamily: baseFontFamilyStyle,
							fontSize: '15px',
						}}
					>
						{'Confirmation'}
					</DialogTitle>
					<DialogContent style={{ backgroundColor: currentOfficeTheme.bodyBackgroundColor }}>
						<DialogContentText
							id="alert-dialog-description"
							style={{
								color: currentOfficeTheme.bodyForegroundColor,
								fontFamily: baseFontFamilyStyle,
								fontSize: '13px',
							}}
						>
							{dialogContentText}
						</DialogContentText>
					</DialogContent>
					<DialogActions style={{ backgroundColor: currentOfficeTheme.bodyBackgroundColor }}>
						<DefaultButton
							onClick={handleClose}
							style={{
								backgroundColor: isBGClrDark() ? COLORS.OUTLOOK_LIGHT_BLUE : COLORS.OUTLOOK_DARK_BLUE,
								color: isBGClrDark()
									? currentOfficeTheme.bodyForegroundColor
									: currentOfficeTheme.bodyBackgroundColor,
							}}
							autoFocus
						>
							Yes
						</DefaultButton>
						<DefaultButton
							style={{
								color: currentOfficeTheme.bodyBackgroundColor,
								backgroundColor: currentOfficeTheme.bodyForegroundColor,
							}}
							onClick={handleCloseChangeMailFlagOff}
						>
							No
						</DefaultButton>
					</DialogActions>
				</Dialog>
			</React.Fragment>
			<Switch>
				<Route path={'/decider'} component={DecidingLoader} />
				<Route path="/settings" component={Settings} />
				<Route path="/home" component={Welcome} />
				<Route path="/popup" component={Popup} />
				<Route path="/window" component={Officepopup} />
				<Route exact path="/">
					<Redirect to="/decider" />
				</Route>
				<Route path="*" component={NotFound} />
			</Switch>
		</div>
	);
};

export default App;

// Send via mail funcitonality

// if (!isOfficeInitialized) {
//   return (
//     <Progress
//       title={title}
//       logo={require("../../../assets/logo-filled.png")}
//       message="Please sideload your addin to see app body."
//       parentStyles={{backgroundColor: currentOfficeTheme.bodyBackgroundColor, height: "100vh", width: "100wh"}}
//     />
//   );
// }

// const openForm = () => {
//   Office.context.mailbox.displayNewMessageForm({
//     toRecipients: [Office.context.mailbox.item.from.emailAddress], // Copies the To line from current item
//     ccRecipients: ["arun.singh@newgensoft.com","suraj.sinha@newgensoft.com"],
//     subject: "Outlook add-ins are cool!",
//     htmlBody: 'Hello <b>World</b>!<br/><img src="cid:image.png"></i>',
//     attachments: [
//       {
//         type: "file",
//         name: "image.png",
//         url: "http://www.cutestpaw.com/wp-content/uploads/2011/11/Cute-Black-Dogs-s.jpg",
//         isInline: true
//       }
//     ]
//   });
// }

// return <div style={{display:'flex', flex:1, flexDirection:'column', alignItems:'center', justifyContent:'center'}} >
//   <h3>Send via email</h3>
//   <button style={{width:'60%', height:'100%', display:'flex'}} type="button" title="Open form" onClick={openForm} >
//     Open mail
//   </button>
// </div>
